import React, { useState, useEffect } from 'react';
import { Camera, Book, Heart, Play, Pause, Crown, Hash, Upload, Users, BookOpen, Settings, Clock, Cloud, Moon, Sun, ChevronRight, Plus, Check, X, Mail, Lock, User, ArrowRight, ChevronLeft } from 'lucide-react';
import { Header } from '../layout/Header';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { StatCard } from '../components/StatCard';
import { BookCard } from '../components/BookCard';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import FindFamiliesModal from '../items/FindFamiliesModal';
import { useToast } from '../hooks/useToast';
import { ViewChildStoriesModal } from '../items/ViewChildStoriesModal';
import { AssignRecordingModal } from '../items/AssignRecordingModal';
import { TokenPurchaseModal } from '../components/CheckoutForm';
import { PersonalBookModal } from '../items/PersonalBookModal';
import { BookViewerModal } from '../items/BookViewerModal';
import API_URL from '../config/API_URL';
import { GrandPStoryRequestsSection } from '../components/GrandParentPremuimBookRequest';
import { SelectBookModal } from '../items/GrandParentSelectBook';
import { PremiumLibrarySection } from '../components/PremuimBookSection';
import { LiveCallModal } from '../video_chat/LiveVideoCallModal';
import io from 'socket.io-client';
import { VideoCallRoom } from '../video_chat/VideoCallRoomComponent';
import { IncomingCallNotification } from '../video_chat/IncomingCallNotification';
import { PhoneCall } from 'lucide-react';

export const GrandparentDashboardPage = ({ userData, onStartRecording }) => {
  const navigate = useNavigate();
  const { showToast, ToastContainer } = useToast();
  const [randomSeed] = useState(() => Date.now().toString(36));

  const [isPersonalBookModalOpen, setIsPersonalBookModalOpen] = useState(false);

  const [personalRecordings, setPersonalRecordings] = useState([]);
  const [loadingPersonalRecordings, setLoadingPersonalRecordings] = useState(true);

  const { user } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [familyCode, setFamilyCode] = useState('');

  const [isFindFamiliesModalOpen, setIsFindFamiliesModalOpen] = useState(false);
  const [tokenPurchaseModal, setTokenPurchaseModal] = useState(false);
  const [isSelectBookModalOpen, setIsSelectBookModalOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  
  const [socket, setSocket] = useState(null);

  const [baseFamilies, setBaseFamilies] = useState([]);
  const [fullQueue, setFullQueue] = useState([]);

  const [showLiveCallModal, setShowLiveCallModal] = useState(false);
  const [activeVideoCall, setActiveVideoCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);

  const [bookViewerModal, setBookViewerModal] = useState({
    isOpen: false,
    book: null,
    familyId: null
  });

  const handlePersonalBookRecord = (book) => {

    navigate(`/record-personal?bookId=${book.id}`);
  };

  // Collapsible sections state
  const [sectionsExpanded, setSectionsExpanded] = useState({
    yourStories: false,
    kidsYouReadTo: false,
    storyLibrary: false,
    storiesFinished: false,
    personalStories: false,
    storiesInProgress: false,
    storiesQueued: false,
    storyRequests: false,
    premiumLibrary: false
  });

  // Real data state
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [books, setBooks] = useState([]);
  const [booksPagination, setBooksPagination] = useState({
    page: 1,
    per_page: 15,
    total_pages: 1,
    total_books: 0,
    has_next: false,
    has_prev: false
  });
  const [queueRequests, setQueueRequests] = useState([]);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [recordings, setRecordings] = useState([]);
  const [loadingRecordings, setLoadingRecordings] = useState(true);
  const [loadingBooks, setLoadingBooks] = useState(true);

  const toggleSection = (section) => {
    setSectionsExpanded(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const filteredBooks = books.filter(book => 
    book.title.toLowerCase().includes(searchQuery) ||
    book.author.toLowerCase().includes(searchQuery)
  );

  useEffect(() => {
    if (!user?.id) return;

    console.log('🔌 Creating socket connection...');
    const newSocket = io(`${API_URL}`, {
      withCredentials: true,
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      console.log('✅ Socket connected:', newSocket.id);
    });

    setSocket(newSocket);

    return () => {
      console.log('🧹 Disconnecting socket');
      newSocket.disconnect();
    };
  }, [user?.id]);

  // useEffect #2 - LISTEN to socket events
  useEffect(() => {
    if (!socket) return;

    socket.on('incoming-video-call', (data) => {
      console.log('📞 Incoming call from:', data);
      setIncomingCall({
        caller: data.caller,
        roomId: data.room_id,
        callId: data.call_id
      });
    });

    socket.on('call-accepted', (data) => {
      console.log('✅ Call accepted:', data);
      setActiveVideoCall({
        roomId: data.room_id,
        participants: data.participants
      });
      setShowLiveCallModal(false);
    });

    socket.on('call-declined', (data) => {
      console.log('❌ Call declined:', data);
      showToast(`${data.decliner_name} declined the call`, 'info');
    });

    socket.on('call-ended', (data) => {
      console.log('📵 Call ended:', data);
      setActiveVideoCall(null);
      showToast('Call ended', 'info');
    });

    return () => {
      socket.off('incoming-video-call');
      socket.off('call-accepted');
      socket.off('call-declined');
      socket.off('call-ended');
    };
  }, [socket]);

  // Fetch families where user is a reader
  useEffect(() => {
    const fetchFamilyData = async () => {
      try {
        const familiesResponse = await fetch(`${API_URL}/api/family/my-families`, {
          credentials: 'include'
        });
        
        if (!familiesResponse.ok) {
          throw new Error('Failed to fetch families');
        }
        
        const familiesData = await familiesResponse.json();
        
        const readerFamilies = familiesData.filter(f => f.my_role === 'reader');
        
        const detailedFamilies = await Promise.all(
          readerFamilies.map(async (family) => {
            const detailResponse = await fetch(`${API_URL}/api/family/${family.id}`, {
              credentials: 'include'
            });
            
            if (!detailResponse.ok) {
              throw new Error(`Failed to fetch family ${family.id} details`);
            }
            
            const data = await detailResponse.json();
            
            const avatarColors = ['#FF6B9D', '#4ECDC4', '#FFD93D', '#A8E6CF', '#FF8B94', '#C7CEEA'];
            
            return {
              id: data.id,
              name: data.family_name,
              kids: (data.kids || []).map((kid, index) => ({
                id: kid.id,
                name: kid.first_name,
                avatar: kid.first_name.charAt(0),
                avatarColor: avatarColors[index % avatarColors.length],
                stories: []
              }))
            };
          })
        );
        
        setBaseFamilies(detailedFamilies);
      } catch (error) {
        console.error('Error fetching family data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFamilyData();
  }, []);

  // Update family stats when recordings and queue data are loaded
  useEffect(() => {
    if (baseFamilies.length > 0) {
      const familiesWithStats = baseFamilies.map(family => ({
        ...family,
        kids: family.kids.map(kid => {
          const recordingsForKid = recordings.filter(recording => 
            recording.kids && recording.kids.some(k => k.id === kid.id)
          ).length;
          
          const queuedForKid = fullQueue.filter(q => q.kid_id === kid.id).length;
          
          return {
            ...kid,
            stats: {
              storiesRecorded: recordingsForKid,
              storiesQueued: queuedForKid,
              timesWatched: 0
            }
          };
        })
      }));
      
      setFamilies(familiesWithStats);
    }
  }, [baseFamilies, recordings, fullQueue]);

  // Fetch books from API with pagination
  useEffect(() => {
    const fetchBooks = async (page = 1, perPage = 15) => {
      setLoadingBooks(true);
      try {
        const response = await fetch(
          `${API_URL}/api/books?page=${page}&per_page=${perPage}&seed=${randomSeed}`,
          { credentials: 'include' }
        );
        if (!response.ok) {
          throw new Error('Failed to fetch books');
        }
        const data = await response.json();
        setBooks(data.books || []);
        setBooksPagination(data.pagination || {
          page: 1,
          per_page: perPage,
          total_pages: 1,
          total_books: 0,
          has_next: false,
          has_prev: false
        });
      } catch (error) {
        console.error('Error fetching books:', error);
        setBooks([]);
      } finally {
        setLoadingBooks(false);
      }
    };

    fetchBooks();
  }, []);

  useEffect(() => {
    const fetchPersonalRecordings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/personal-books/my-recordings`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch personal recordings');
        }
        
        const data = await response.json();
        setPersonalRecordings(data);
      } catch (error) {
        console.error('Error fetching personal recordings:', error);
      } finally {
        setLoadingPersonalRecordings(false);
      }
    };

    fetchPersonalRecordings();
  }, []);

  const handlePageChange = (newPage) => {
    fetchBooks(newPage, booksPagination.per_page);
  };

  const handlePerPageChange = (newPerPage) => {
    fetchBooks(1, newPerPage);
  };

  const handleAcceptCall = async (roomId) => {
    try {
      const response = await fetch(`${API_URL}/api/video-call/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          room_id: roomId,
          user_id: user.id
        })
      });

      const data = await response.json();
      if (data.success) {
        setActiveVideoCall({
          roomId: roomId,
          participants: data.participants
        });
        setIncomingCall(null);
      }
    } catch (error) {
      console.error('Failed to accept call:', error);
      showToast('Failed to join call', 'error');
    }
  };

  const handleDeclineCall = async (roomId) => {
    try {
      await fetch(`${API_URL}/api/video-call/decline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          room_id: roomId,
          user_id: user.id
        })
      });

      setIncomingCall(null);
    } catch (error) {
      console.error('Failed to decline call:', error);
    }
  };

  const handleLeaveCall = () => {
    setActiveVideoCall(null);
  };



  const handleDeletePersonalRecording = async (recordingId) => {
    if (!window.confirm('Are you sure you want to delete this recording?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/personal-books/delete-recording/${recordingId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        showToast('Recording deleted successfully', 'success');
        // Remove from state
        setPersonalRecordings(prev => prev.filter(r => r.id !== recordingId));
      } else {
        showToast('Failed to delete recording', 'error');
      }
    } catch (error) {
      console.error('Error deleting recording:', error);
      showToast('Error deleting recording', 'error');
    }
  };

  const fetchBooks = async (page = 1, perPage = 15) => {
    setLoadingBooks(true);
    try {
      const response = await fetch(
        `${API_URL}/api/books?page=${page}&per_page=${perPage}&seed=${randomSeed}`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch books');
      }
      const data = await response.json();
      setBooks(data.books || []);
      setBooksPagination(data.pagination || {
        page: 1,
        per_page: perPage,
        total_pages: 1,
        total_books: 0,
        has_next: false,
        has_prev: false
      });
    } catch (error) {
      console.error('Error fetching books:', error);
      setBooks([]);
    } finally {
      setLoadingBooks(false);
    }
  };

  useEffect(() => {
    const fetchQueueRequests = async () => {
      try {
        const response = await fetch(`${API_URL}/api/queue/for-reader`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch queue requests');
        }
        
        const data = await response.json();
        setFullQueue(data);

        const requestedStories = data.filter(q => q.type === 'requested');
        setQueueRequests(requestedStories);
      } catch (error) {
        console.error('Error fetching queue requests:', error);
      } finally {
        setLoadingQueue(false);
      }
    };

    fetchQueueRequests();
  }, []);

  useEffect(() => {
    const fetchRecordings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/recording/my-recordings`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch recordings');
        }
        
        const data = await response.json();
        setRecordings(data);
      } catch (error) {
        console.error('Error fetching recordings:', error);
      } finally {
        setLoadingRecordings(false);
      }
    };

    fetchRecordings();
  }, []);

  const handleJoinFamily = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await fetch(`${API_URL}/api/family/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ family_code: familyCode.trim().toUpperCase() })
      });

      if (response.ok) {
        setSuccess(true);
        setFamilyCode('');
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to join family. Please check the code and try again.');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const [viewStoriesModal, setViewStoriesModal] = useState({ isOpen: false, childId: null, childName: '' });
  const [assignModal, setAssignModal] = useState({ isOpen: false, childId: null, childName: '' });

  const finishedStories = recordings.filter(r => r.processing_status === 'ready');

  const allStories = families.flatMap(family => 
    family.kids.flatMap(kid => kid.stories)
  );
  
  const aggregateStats = {
    storiesRecorded: recordings.length,
    timesWatched: families.reduce((sum, family) => 
      sum + family.kids.reduce((s, kid) => s + kid.stats.timesWatched, 0), 0
    ),
    happyGrandkids: families.reduce((sum, family) => sum + family.kids.length, 0),
    storiesQueued: families.reduce((sum, family) => 
      sum + family.kids.reduce((s, kid) => s + kid.stats.storiesQueued, 0), 0
    )
  };

  const handleSelectBook = (book) => {
    setIsSelectBookModalOpen(false);
    navigate(`/book/${book.id}`);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontSize: '1.25rem', color: '#6b7280' }}>Loading your story library...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)' }}>
      <Header userName={`${user.first_name} ${user.last_name}`} userType="grandparent" />
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '2rem 1.5rem' }}>
        {/* Hero Section */}
      <Card style={{ background: 'linear-gradient(90deg, #9333ea 0%, #7e22ce 100%)', color: 'white', marginBottom: '2rem', padding: '2rem 2rem 2rem 2rem' }}>
        <div style={{ display: 'flex', gap: '2rem', alignItems: 'stretch' }}>
          {/* Left side - existing content */}
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: '1.875rem', fontWeight: 'bold', marginBottom: '0.75rem' }}>Your Story Library</h2>
            <p style={{ color: '#e9d5ff', fontSize: '1.125rem', marginBottom: '1.5rem' }}>
              Record stories for {aggregateStats.happyGrandkids} kids across {families.length} families
            </p>
            <Button 
              onClick={() => setIsSelectBookModalOpen(true)}
              variant="ghost"
              style={{ marginRight: '1rem' }}
            >
              <Plus style={{ width: '1.25rem', height: '1.25rem' }} />
              Record New Story
            </Button>
            <Button 
              onClick={() => setIsFindFamiliesModalOpen(true)}
              variant="secondary"
              style={{
                background: 'white',
                color: '#9333ea',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                fontWeight: '600'
              }}
            >
              <Users style={{ width: '1.25rem', height: '1.25rem' }} />
              Find Families
            </Button>
              <Button 
                onClick={() => setIsPersonalBookModalOpen(true)}
                variant="reader"
                style={{
                  marginTop: '.5rem',
                  background: '#33bd8fff',
                  color: 'white',
                  fontWeight: '600'
                }}
              >
                <BookOpen style={{ width: '1.25rem', height: '1.25rem' }} />
                Record Story you Own
              </Button>
              <Button 
                onClick={() => setShowLiveCallModal(true)}
                variant="secondary"
                style={{
                  marginLeft: '0.5rem',
                  background: '#10b981',
                  color: 'white',
                  fontWeight: '600'
                }}
              >
                <PhoneCall style={{ width: '1.25rem', height: '1.25rem' }} />
                Live Call
              </Button>
          </div>

          {/* Right side - Join a Family section */}
          <div style={{ flex: 1 }}>
            <div style={{ 
              background: 'linear-gradient(135deg, #fef3c7 0%, #fce7f3 100%)', 
              padding: '1rem 1rem 1rem 1rem', 
              borderRadius: '12px',
              height: '85%'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                <Hash size={20} color="#db2777" />
                <h4 style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#1f2937' }}>
                  Join a Family
                </h4>
              </div>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '-1rem', marginBottom: '1rem'}}>
                Enter the family invite code shared by parents to start reading stories to their kids
              </p>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  color: '#1f2937',
                  fontSize: '0.875rem'
                }}>
                  Family Invite Code
                </label>
                <input
                  type="text"
                  value={familyCode}
                  onChange={(e) => setFamilyCode(e.target.value.toUpperCase())}
                  maxLength={6}
                  style={{ 
                    width: '100%', 
                    padding: '0.75rem', 
                    borderRadius: '8px', 
                    border: error ? '2px solid #dc2626' : '1px solid #d1d5db',
                    fontSize: '1.25rem',
                    fontWeight: 'bold',
                    letterSpacing: '0.1em',
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    background: 'white',
                    boxSizing: 'border-box'
                  }}
                  placeholder="ABC123"
                />
                {error && (
                  <p style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    {error}
                  </p>
                )}
                {success && (
                  <p style={{ color: '#059669', fontSize: '0.875rem', marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Check size={16} /> Successfully joined family!
                  </p>
                )}
              </div>

              <Button 
                onClick={handleJoinFamily}
                disabled={loading || familyCode.length < 6}
                style={{ 
                  width: '100%',
                  background: loading || familyCode.length < 6 ? '#d1d5db' : '#db2777',
                  cursor: loading || familyCode.length < 6 ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Joining...' : 'Join Family'}
              </Button>
            </div>
          </div>
        </div>
      </Card>

        {/* Stats Section */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
          <StatCard value={aggregateStats.storiesRecorded} label="Stories Recorded" color="#9333ea" />
          {/* <StatCard value={aggregateStats.timesWatched} label="Times Watched" color="#db2777" /> */}
          <StatCard value={aggregateStats.happyGrandkids} label="Happy Grandkids" color="#2563eb" />
          <StatCard value={aggregateStats.storiesQueued} label="Stories Queued" color="#059669" />
        </div>

        {/* Your Stories Section - Collapsible */}
        <Card style={{ padding: '1.5rem', marginBottom: '2rem' }}>
          <div 
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: sectionsExpanded.yourStories ? '1.5rem' : '0' }}
            onClick={() => toggleSection('yourStories')}
          >
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937' }}>Your Stories</h3>
            <ChevronRight size={24} style={{ transform: sectionsExpanded.yourStories ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
          </div>
          
          {sectionsExpanded.yourStories && (
            <>
              {/* Stories Finished */}
              <div style={{ marginBottom: '2rem' }}>
                <div 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    cursor: 'pointer',
                    marginBottom: sectionsExpanded.personalStories ? '1rem' : '0'
                  }}
                  onClick={() => toggleSection('personalStories')}
                >
                  <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#10b981', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <BookOpen size={20} />
                    Personal Stories ({personalRecordings.length})
                  </h4>
                  <ChevronRight size={20} style={{ transform: sectionsExpanded.personalStories ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                </div>
                
                {sectionsExpanded.personalStories && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {loadingPersonalRecordings ? (
                      <p style={{ color: '#6b7280', fontStyle: 'italic' }}>Loading personal recordings...</p>
                    ) : personalRecordings.length > 0 ? (
                      personalRecordings.map(recording => (
                        <div 
                          key={`personal-${recording.id}`} 
                          style={{
                            border: '2px solid #10b981',
                            borderRadius: '0.75rem',
                            padding: '1rem',
                            background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                            position: 'relative'
                          }}
                        >
                          {/* Personal Book Badge */}
                          <div style={{
                            position: 'absolute',
                            top: '1rem',
                            right: '1rem',
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            color: 'white',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.625rem',
                            fontWeight: '700',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}>
                            <BookOpen size={12} />
                            PERSONAL
                          </div>

                          <div style={{ display: 'flex', gap: '1rem', paddingRight: '5rem' }}>
                            {recording.book?.cover_image_url && (
                              <img 
                                src={recording.book.cover_image_url} 
                                alt={recording.book.title}
                                style={{
                                  width: '60px',
                                  height: '80px',
                                  objectFit: 'cover',
                                  borderRadius: '0.375rem',
                                  border: '1px solid #10b981'
                                }}
                              />
                            )}
                            <div style={{ flex: 1 }}>
                              <h5 style={{ 
                                fontWeight: '600', 
                                fontSize: '1.125rem', 
                                marginBottom: '0.25rem', 
                                color: '#064e3b'
                              }}>
                                {recording.book?.title || 'Untitled Book'}
                              </h5>
                              <p style={{ 
                                fontSize: '0.875rem', 
                                color: '#047857',
                                marginBottom: '0.5rem' 
                              }}>
                                by {recording.book?.author || 'Unknown'}
                              </p>
                              <p style={{ 
                                fontSize: '0.75rem', 
                                color: '#065f46'
                              }}>
                                Type: {recording.recording_type === 'audio_only' ? '🎙️ Audio Only' : '📹 Video'}
                              </p>
                              {recording.kids && recording.kids.length > 0 && (
                                <p style={{ 
                                  fontSize: '0.75rem', 
                                  color: '#065f46',
                                  marginTop: '0.25rem'
                                }}>
                                  Assigned to: {recording.kids.map(k => k.name).join(', ')}
                                </p>
                              )}
                              {recording.duration_seconds && (
                                <p style={{ 
                                  fontSize: '0.75rem', 
                                  color: '#065f46',
                                  marginTop: '0.25rem' 
                                }}>
                                  Duration: {Math.floor(recording.duration_seconds / 60)}:{(recording.duration_seconds % 60).toString().padStart(2, '0')}
                                </p>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                              <Button
                                onClick={() => navigate(`/personal-recording/${recording.id}`)}
                                variant="primary"
                                style={{
                                  background: 'linear-gradient(135deg, #10b981, #059669)'
                                }}
                              >
                                View <ArrowRight size={16} />
                              </Button>
                              <Button
                                onClick={() => handleDeletePersonalRecording(recording.id)}
                                variant="secondary"
                                style={{
                                  background: '#ef4444',
                                  color: 'white',
                                  fontSize: '0.75rem',
                                  padding: '0.25rem 0.5rem'
                                }}
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No personal book recordings yet</p>
                    )}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '2rem' }}>
                <div 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    cursor: 'pointer',
                    marginBottom: sectionsExpanded.storiesFinished ? '1rem' : '0'
                  }}
                  onClick={() => toggleSection('storiesFinished')}
                >
                  <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#059669', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Check size={20} />
                    Stories Finished ({finishedStories.length})
                  </h4>
                  <ChevronRight size={20} style={{ transform: sectionsExpanded.storiesFinished ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                </div>
                
                {sectionsExpanded.storiesFinished && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {loadingRecordings ? (
                      <p style={{ color: '#6b7280', fontStyle: 'italic' }}>Loading recordings...</p>
                    ) : finishedStories.length > 0 ? (
                      finishedStories.map(recording => (
                        <div 
                          key={recording.id} 
                          style={{
                            border: recording.is_premium ? '2px solid #fbbf24' : '1px solid #e5e7eb',
                            borderRadius: '0.75rem',
                            padding: '1rem',
                            background: recording.is_premium 
                              ? 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)'
                              : 'white',
                            position: 'relative'
                          }}
                        >
                          {/* Premium Badge */}
                          {recording.is_premium && (
                            <div style={{
                              position: 'absolute',
                              top: '1rem',
                              right: '1rem',
                              background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                              color: 'white',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '0.25rem',
                              fontSize: '0.625rem',
                              fontWeight: '700',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}>
                              <Crown size={12} />
                              PREMIUM
                            </div>
                          )}

                          <div style={{ display: 'flex', gap: '1rem', paddingRight: recording.is_premium ? '4rem' : '0' }}>
                            <div style={{ flex: 1 }}>
                              <h5 style={{ 
                                fontWeight: '600', 
                                fontSize: '1.125rem', 
                                marginBottom: '0.25rem', 
                                color: recording.is_premium ? '#92400e' : '#111827'
                              }}>
                                {recording.book?.title || 'Unknown Book'}
                              </h5>
                              <p style={{ 
                                fontSize: '0.875rem', 
                                color: recording.is_premium ? '#92400e' : '#6b7280',
                                marginBottom: '0.5rem' 
                              }}>
                                by {recording.book?.author || 'Unknown'}
                              </p>
                              <p style={{ 
                                fontSize: '0.75rem', 
                                color: recording.is_premium ? '#78350f' : '#6b7280'
                              }}>
                                Recorded for: {recording.kids.map(k => `${k.name} (${k.family_name})`).join(', ')}
                              </p>
                              <p style={{ 
                                fontSize: '0.75rem', 
                                color: recording.is_premium ? '#78350f' : '#6b7280',
                                marginTop: '0.25rem' 
                              }}>
                                Duration: {Math.floor(recording.duration_seconds / 60)}:{(recording.duration_seconds % 60).toString().padStart(2, '0')}
                              </p>
                            </div>
                            <Button
                              onClick={() => navigate(`/recording/${recording.id}`)}
                              variant="primary"
                              style={{
                                background: recording.is_premium 
                                  ? 'linear-gradient(135deg, #fbbf24, #f59e0b)'
                                  : undefined
                              }}
                            >
                              View <ArrowRight size={16} />
                            </Button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No finished stories yet</p>
                    )}
                  </div>
                )}
              </div>

              {/* Stories In Progress */}
              <div style={{ marginBottom: '2rem' }}>
                <div 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    cursor: 'pointer',
                    marginBottom: sectionsExpanded.storiesInProgress ? '1rem' : '0'
                  }}
                  onClick={() => toggleSection('storiesInProgress')}
                >
                  <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Play size={20} />
                    Stories In Progress ({allStories.filter(s => s.status === 'in-progress').length})
                  </h4>
                  <ChevronRight size={20} style={{ transform: sectionsExpanded.storiesInProgress ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                </div>
                
                {sectionsExpanded.storiesInProgress && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {allStories.filter(s => s.status === 'in-progress').length > 0 ? (
                      allStories.filter(s => s.status === 'in-progress').map(story => (
                        <BookCard
                          key={story.id}
                          {...story}
                          onAction={() => onStartRecording(story)}
                        />
                      ))
                    ) : (
                      <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No stories in progress</p>
                    )}
                  </div>
                )}
              </div>

              {/* Stories Queued */}
              <GrandPStoryRequestsSection
                  queueRequests={queueRequests}
                  loadingQueue={loadingQueue}
                  sectionsExpanded={sectionsExpanded}
                  toggleSection={toggleSection}
                  navigate={navigate}
                />
            </>
          )}
        </Card>

        {/* Kids You Read To Section - Collapsible */}
        <Card style={{ padding: '1.5rem', marginBottom: '2rem' }}>
          <div 
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: sectionsExpanded.kidsYouReadTo ? '1.5rem' : '0' }}
            onClick={() => toggleSection('kidsYouReadTo')}
          >
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937' }}>Kids You Read To</h3>
            <ChevronRight size={24} style={{ transform: sectionsExpanded.kidsYouReadTo ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
          </div>
          
          {sectionsExpanded.kidsYouReadTo && (
            <>
              {families.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p style={{ color: '#6b7280', marginBottom: '1rem' }}>You haven't connected with any families yet</p>
                  <Button onClick={() => setIsFindFamiliesModalOpen(true)}>
                    <Users size={20} /> Find Families to Read To
                  </Button>
                </div>
              ) : (
                families.map(family => (
                  <div key={family.id} style={{ marginBottom: '2rem' }}>
                    <h4 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>{family.name}</h4>
                    {family.kids.length === 0 ? (
                      <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No kids in this family yet</p>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1.5rem' }}>
                        {family.kids.map(child => (
                          <div
                            key={child.id}
                            style={{
                              border: '1px solid #e5e7eb',
                              borderRadius: '0.75rem',
                              padding: '1.25rem',
                              background: 'white',
                              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                              cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.02)';
                              e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)';
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                              <div style={{
                                fontSize: '2rem',
                                fontWeight: 'bold',
                                background: `linear-gradient(135deg, ${child.avatarColor}, ${child.avatarColor}dd)`,
                                borderRadius: '50%',
                                width: '60px',
                                height: '60px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                textTransform: 'uppercase',
                                border: '3px solid white'
                              }}>
                                {child.avatar}
                              </div>
                              <div>
                                <h4 style={{ fontWeight: '700', color: '#1f2937', fontSize: '1.25rem' }}>{child.name}</h4>
                                <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                  {child.stats.storiesRecorded} recorded, {child.stats.storiesQueued} queued
                                </p>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', maxWidth: '100%' }}>
                              <Button
                                onClick={() => setViewStoriesModal({ isOpen: true, childId: child.id, childName: child.name })}
                                variant="primary"
                                style={{
                                  flex: 1,
                                  minWidth: '80px',
                                  background: '#9333ea',
                                  color: 'white',
                                  padding: '0.5rem',
                                  fontSize: '0.875rem'
                                }}
                              >
                                View Stories <ArrowRight size={16} />
                              </Button>
                              <Button
                                onClick={() => setAssignModal({ isOpen: true, childId: child.id, childName: child.name })}
                                variant="secondary"
                                style={{
                                  flex: 1,
                                  minWidth: '80px',
                                  background: '#e5e7eb',
                                  color: '#1f2937',
                                  padding: '0.5rem',
                                  fontSize: '0.875rem'
                                }}
                              >
                                Add Story <Plus size={16} />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </>
          )}
        </Card>

        {/* Story Library Section - Collapsible */}
        <Card style={{ padding: '1.5rem' }}>
          <div 
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: sectionsExpanded.storyLibrary ? '1.5rem' : '0' }}
            onClick={() => toggleSection('storyLibrary')}
          >
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937' }}>Story Library</h3>
            {sectionsExpanded.storyLibrary && (
                <input
                  type="text"
                  placeholder="Search by title or author..."
                  value={searchQuery}
                  onChange={handleSearch}
                  onClick={(e) => e.stopPropagation()} // Prevent collapse when clicking input
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    fontSize: '1rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
            )}
            <ChevronRight size={24} style={{ transform: sectionsExpanded.storyLibrary ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
          </div>
          
          {sectionsExpanded.storyLibrary && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <p style={{ color: '#4b5563', margin: 0 }}>
                  Choose a book to record or send a request to a family member.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Show:</span>
                  <select
                    value={booksPagination.per_page}
                    onChange={(e) => handlePerPageChange(Number(e.target.value))}
                    style={{
                      padding: '0.375rem 0.75rem',
                      borderRadius: '0.375rem',
                      border: '1px solid #d1d5db',
                      fontSize: '0.875rem',
                      cursor: 'pointer'
                    }}
                  >
                    <option value={15}>15</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                  <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>per page</span>
                </div>
                </div>
                {loadingBooks ? (
                  <p style={{ color: '#6b7280', textAlign: 'center' }}>Loading books...</p>
                ) : filteredBooks.length === 0 ? (
                  searchQuery ? (
                    <p style={{ color: '#6b7280', textAlign: 'center' }}>No books match your search "{searchQuery}"</p>
                  ) : (
                    <p style={{ color: '#6b7280', textAlign: 'center' }}>No books available yet.</p>
                  )
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1.5rem' }}>
                    {filteredBooks.map(book => (
                      <div
                      key={book.id}
                      style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.75rem',
                        padding: '1.25rem',
                        textAlign: 'center',
                        background: 'white',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        gap: '1rem'
                      }}
                    >
                      <div>
                        {book.pdf_url ? (
                          <div style={{ 
                            width: '100%', 
                            height: '250px', 
                            marginBottom: '0.75rem',
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.5rem',
                            overflow: 'hidden',
                            backgroundColor: '#f9fafb'
                          }}>
                            <iframe 
                              src={`${book.pdf_url.trim()}#page=1&view=FitH`}
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                border: 'none',
                                pointerEvents: 'none'
                              }}
                              title={`${book.title} preview`}
                            />
                          </div>
                        ) : (
                          <div style={{ fontSize: '3.5rem', marginBottom: '0.75rem' }}>📖</div>
                        )}
                        <h4 style={{ fontWeight: '600', color: '#1f2937', fontSize: '1.125rem' }}>{book.title}</h4>
                        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>by {book.author}</p>
                        {book.description && (
                          <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.5rem' }}>{book.description}</p>
                        )}
                      </div>
                      <Button 
                        onClick={() => navigate(`/book/${book.id}`)} 
                        variant="primary" 
                        style={{ width: '100%' }}
                      >
                        View Story <ArrowRight size={16} style={{ marginLeft: '0.5rem' }} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                paddingTop: '1.5rem',
                borderTop: '1px solid #e5e7eb',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  {searchQuery ? (
                    `Showing ${filteredBooks.length} book${filteredBooks.length !== 1 ? 's' : ''} matching "${searchQuery}"`
                  ) : (
                    `Showing ${((booksPagination.page - 1) * booksPagination.per_page) + 1} to ${Math.min(booksPagination.page * booksPagination.per_page, booksPagination.total_books)} of ${booksPagination.total_books} books`
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <button
                    onClick={() => handlePageChange(booksPagination.page - 1)}
                    disabled={!booksPagination.has_prev}
                    style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '0.375rem',
                      border: '1px solid #d1d5db',
                      background: booksPagination.has_prev ? 'white' : '#f3f4f6',
                      color: booksPagination.has_prev ? '#374151' : '#9ca3af',
                      cursor: booksPagination.has_prev ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      fontSize: '0.875rem'
                    }}
                  >
                    <ChevronLeft size={16} /> Previous
                  </button>
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    {Array.from({ length: booksPagination.total_pages }, (_, i) => i + 1)
                      .filter(pageNum => {
                        return pageNum === 1 || 
                               pageNum === booksPagination.total_pages ||
                               Math.abs(pageNum - booksPagination.page) <= 1;
                      })
                      .map((pageNum, index, array) => {
                        const showEllipsis = index > 0 && pageNum - array[index - 1] > 1;
                        return (
                          <React.Fragment key={pageNum}>
                            {showEllipsis && (
                              <span style={{ padding: '0.5rem', color: '#9ca3af' }}>...</span>
                            )}
                            <button
                              onClick={() => handlePageChange(pageNum)}
                              style={{
                                padding: '0.5rem 0.75rem',
                                borderRadius: '0.375rem',
                                border: '1px solid #d1d5db',
                                background: pageNum === booksPagination.page ? '#9333ea' : 'white',
                                color: pageNum === booksPagination.page ? 'white' : '#374151',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: pageNum === booksPagination.page ? 'bold' : 'normal',
                                minWidth: '2.5rem'
                              }}
                            >
                              {pageNum}
                            </button>
                          </React.Fragment>
                        );
                      })}
                  </div>
                  <button
                    onClick={() => handlePageChange(booksPagination.page + 1)}
                    disabled={!booksPagination.has_next}
                    style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '0.375rem',
                      border: '1px solid #d1d5db',
                      background: booksPagination.has_next ? 'white' : '#f3f4f6',
                      color: booksPagination.has_next ? '#374151' : '#9ca3af',
                      cursor: booksPagination.has_next ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      fontSize: '0.875rem'
                    }}
                  >
                    Next <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </>
          )}
        </Card>
        <PremiumLibrarySection
          isExpanded={sectionsExpanded.premiumLibrary}
          onToggle={() => toggleSection('premiumLibrary')}
          familyData={families[0]}
          showToast={showToast}
          navigate={navigate}
          onOpenBookViewer={(book) => {
            navigate(`/book/${book.id}?premium=true`);
          }}
          onOpenTokenPurchase={() => setTokenPurchaseModal(true)}
        />
        <FindFamiliesModal 
          isOpen={isFindFamiliesModalOpen}
          onClose={() => setIsFindFamiliesModalOpen(false)}
        />
        <ViewChildStoriesModal
          isOpen={viewStoriesModal.isOpen}
          onClose={() => setViewStoriesModal({ isOpen: false, childId: null, childName: '' })}
          childId={viewStoriesModal.childId}
          childName={viewStoriesModal.childName}
          queuedStories={fullQueue}
        />
        <AssignRecordingModal
          isOpen={assignModal.isOpen}
          onClose={() => setAssignModal({ isOpen: false, childId: null, childName: '' })}
          childId={assignModal.childId}
          childName={assignModal.childName}
          onAssignSuccess={() => {
            // Optionally refresh the dashboard data
          }}
        />
        <SelectBookModal
          isOpen={isSelectBookModalOpen}
          onClose={() => setIsSelectBookModalOpen(false)}
          onSelectBook={handleSelectBook}
        />
        <TokenPurchaseModal
          isOpen={tokenPurchaseModal}
          onClose={() => setTokenPurchaseModal(false)}
          showToast={showToast}
          onPurchaseComplete={() => {
            // Optionally refresh premium status after purchase
            showToast('Token purchase successful!', 'success');
          }}
        />
        <PersonalBookModal
          isOpen={isPersonalBookModalOpen}
          onClose={() => setIsPersonalBookModalOpen(false)}
          onProceedToRecord={handlePersonalBookRecord}
        />
        <LiveCallModal
          isOpen={showLiveCallModal}
          onClose={() => setShowLiveCallModal(false)}
          familyId={families[0]?.id}
          currentUser={user}
          socket={socket}  // ⭐ ADD THIS LINE
          onCallInitiated={(data) => {
            console.log('✅ Call accepted, joining video room:', data);
            setActiveVideoCall({
              roomId: data.roomId,
              participants: data.participants
            });
            setShowLiveCallModal(false);  // ⭐ ADD THIS LINE
            showToast('Joining call...', 'success');
          }}
        />

        {/* Incoming Call Notification */}
        {incomingCall && (
          <IncomingCallNotification
            caller={incomingCall.caller}
            roomId={incomingCall.roomId}
            onAccept={handleAcceptCall}
            onDecline={handleDeclineCall}
          />
        )}

        {/* Active Video Call Room */}
        {activeVideoCall && (
          <VideoCallRoom
            roomId={activeVideoCall.roomId}
            currentUser={user}
            onLeave={handleLeaveCall}
            participants={activeVideoCall.participants}
          />
        )}

      </div>
    </div>
  );
};